import React, { useState, useEffect, useRef } from 'react';
import { updateTask } from '../../../utils/api';
import './TaskDetailSidePanel.scss';

const TaskRecurringSection = ({ 
    taskData, 
    projectId, 
    onRefreshMainTaskData,
    onRefreshTaskData,
    style
}) => {
    const [isRecurring, setIsRecurring] = useState(false);
    const [recurrenceType, setRecurrenceType] = useState(0); // 0=no repeat, 1=weekly, 2=monthly, 3=annually, 4=daily
    const [recurrenceInterval, setRecurrenceInterval] = useState(1); // Every X days/weeks/months
    const [recurrenceStartDate, setRecurrenceStartDate] = useState('');
    const [recurrenceEndDate, setRecurrenceEndDate] = useState('');
    const [showRecurrenceStartDatePicker, setShowRecurrenceStartDatePicker] = useState(false);
    const [showRecurrenceEndDatePicker, setShowRecurrenceEndDatePicker] = useState(false);
    const [recurrenceDays, setRecurrenceDays] = useState([]); // Selected days for weekly (0=Sunday, 1=Monday, etc.)
    const [originalRecurringData, setOriginalRecurringData] = useState({
        isRecurring: false,
        recurrenceType: 0,
        recurrenceInterval: 1,
        recurrenceStartDate: '',
        recurrenceEndDate: '',
        recurrenceDays: []
    });

    const recurrenceStartDatePickerRef = useRef(null);
    const recurrenceEndDatePickerRef = useRef(null);

    const weekDays = [
        { index: 0, label: 'Sun', fullLabel: 'Sunday' },
        { index: 1, label: 'Mon', fullLabel: 'Monday' },
        { index: 2, label: 'Tue', fullLabel: 'Tuesday' },
        { index: 3, label: 'Wed', fullLabel: 'Wednesday' },
        { index: 4, label: 'Thu', fullLabel: 'Thursday' },
        { index: 5, label: 'Fri', fullLabel: 'Friday' },
        { index: 6, label: 'Sat', fullLabel: 'Saturday' }
    ];

    // Initialize recurring data from taskData
    useEffect(() => {
        if (taskData) {
            const recurrent = taskData.recurrent !== undefined ? taskData.recurrent : 0;
            const isRecurringValue = recurrent !== 0 && recurrent !== 9;
            
            // Read from metas.recurrence if available, otherwise fall back to taskData.recurrence
            const recurrenceMeta = taskData.metas?.recurrence || taskData.recurrence;
            
            const recurrenceIntervalValue = recurrenceMeta?.repeat || 1;
            const recurrenceStartDateValue = recurrenceMeta?.repeat_year || recurrenceMeta?.start_date || '';
            const recurrenceEndDateValue = recurrenceMeta?.expire_after_date || recurrenceMeta?.expire_date || '';
            
            // Extract checked days from weekdays array
            let recurrenceDaysArray = [];
            if (recurrenceMeta?.weekdays && Array.isArray(recurrenceMeta.weekdays)) {
                recurrenceDaysArray = recurrenceMeta.weekdays
                    .filter(day => {
                        // Handle both string and boolean values
                        const checked = day.checked;
                        return checked === true || checked === 'true' || checked === 1 || checked === '1';
                    })
                    .map(day => parseInt(day.value, 10))
                    .filter(day => !isNaN(day));
            } else {
                // Fallback to old format
                const days = recurrenceMeta?.days || recurrenceMeta?.by_day || [];
                recurrenceDaysArray = Array.isArray(days) ? days.map(d => parseInt(d, 10)) : [];
            }

            setIsRecurring(isRecurringValue);
            setRecurrenceType(recurrent);
            setRecurrenceInterval(recurrenceIntervalValue);
            setRecurrenceStartDate(recurrenceStartDateValue);
            setRecurrenceEndDate(recurrenceEndDateValue);
            setRecurrenceDays(recurrenceDaysArray);

            // Store original recurring data for comparison
            setOriginalRecurringData({
                isRecurring: isRecurringValue,
                recurrenceType: recurrent,
                recurrenceInterval: recurrenceIntervalValue,
                recurrenceStartDate: recurrenceStartDateValue,
                recurrenceEndDate: recurrenceEndDateValue,
                recurrenceDays: recurrenceDaysArray
            });
        }
    }, [taskData]);

    // Close date picker when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (showRecurrenceStartDatePicker && 
                recurrenceStartDatePickerRef.current && 
                !recurrenceStartDatePickerRef.current.contains(event.target) &&
                !event.target.closest('.task-detail-recurrence-start-date-wrapper')) {
                setShowRecurrenceStartDatePicker(false);
            }
            if (showRecurrenceEndDatePicker && 
                recurrenceEndDatePickerRef.current && 
                !recurrenceEndDatePickerRef.current.contains(event.target) &&
                !event.target.closest('.task-detail-recurrence-end-date-wrapper')) {
                setShowRecurrenceEndDatePicker(false);
            }
        };

        if (showRecurrenceStartDatePicker || showRecurrenceEndDatePicker) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showRecurrenceStartDatePicker, showRecurrenceEndDatePicker]);

    // Format date for input
    const formatDateForInput = (dateValue) => {
        if (!dateValue) return '';
        try {
            if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
                return dateValue;
            }
            const date = new Date(dateValue);
            if (isNaN(date.getTime())) return '';
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        } catch (e) {
            return '';
        }
    };

    const handleRecurringToggle = async () => {
        const newIsRecurring = !isRecurring;
        setIsRecurring(newIsRecurring);
        if (!newIsRecurring) {
            await handleDisableRecurring();
            // If disabling, reset to defaults
            setRecurrenceType(0);
            setRecurrenceInterval(1);
            setRecurrenceStartDate('');
            setRecurrenceEndDate('');
            setRecurrenceDays([]);
        } else {
            // If enabling, set default to daily
            setRecurrenceType(4);
        }
    };

    const handleRecurrenceTypeChange = (newType) => {
        const typeValue = parseInt(newType, 10);
        setRecurrenceType(typeValue);
        // Clear days if changing from weekly to another type
        if (typeValue !== 1) {
            setRecurrenceDays([]);
        }
    };

    const handleRecurrenceIntervalChange = (newInterval) => {
        const intervalValue = parseInt(newInterval, 10) || 1;
        setRecurrenceInterval(intervalValue);
    };

    const handleRecurrenceStartDateChange = (newDate) => {
        setRecurrenceStartDate(newDate);
        //setShowRecurrenceStartDatePicker(false);
    };

    const handleRecurrenceEndDateChange = (newDate) => {
        setRecurrenceEndDate(newDate);
        //setShowRecurrenceEndDatePicker(false);
    };

    const handleRecurrenceDayToggle = (dayIndex) => {
        const newDays = recurrenceDays.includes(dayIndex)
            ? recurrenceDays.filter(d => d !== dayIndex)
            : [...recurrenceDays, dayIndex].sort();
        setRecurrenceDays(newDays);
    };

    // Check if recurring data has changed
    const hasRecurringChanges = () => {
        const currentRecurrent = isRecurring ? recurrenceType : 0;
        return (
            (isRecurring !== originalRecurringData.isRecurring) ||
            (currentRecurrent !== originalRecurringData.recurrenceType) ||
            (recurrenceInterval !== originalRecurringData.recurrenceInterval) ||
            (recurrenceStartDate !== originalRecurringData.recurrenceStartDate) ||
            (recurrenceEndDate !== originalRecurringData.recurrenceEndDate) ||
            (JSON.stringify(recurrenceDays.sort()) !== JSON.stringify(originalRecurringData.recurrenceDays.sort()))
        );
    };

    const handleDisableRecurring = async () => {
        const currentTask = taskData;
        if (!currentTask?.id || !projectId) return;

        const updateData = {
            recurrent: 0,
            action: 'disable_recurring'
        };

        await updateTask(projectId, currentTask.id, updateData);

        if(typeof onRefreshTaskData === "function") {
            await onRefreshTaskData();
        }

    };

    // Save all recurring changes
    const handleSaveRecurring = async () => {
        const currentTask = taskData;
        if (!currentTask?.id || !projectId) return;

        const recurrentValue = isRecurring ? recurrenceType : 0;
        
        const updateData = {
            recurrent: recurrentValue,
            action: 'update_recurring'
        };

        if (isRecurring) {
            updateData.recurrence_interval = recurrenceInterval;
            if (recurrenceStartDate) {
                updateData.recurrence_start_date = recurrenceStartDate;
            }
            if (recurrenceEndDate) {
                updateData.recurrence_end_date = recurrenceEndDate;
            }
            if (recurrenceType === 1 && recurrenceDays.length > 0) {
                updateData.recurrence_days = recurrenceDays;
            }
        }

        await updateTask(projectId, currentTask.id, updateData);

        // Update original data after save
        setOriginalRecurringData({
            isRecurring: isRecurring,
            recurrenceType: recurrentValue,
            recurrenceInterval: recurrenceInterval,
            recurrenceStartDate: recurrenceStartDate,
            recurrenceEndDate: recurrenceEndDate,
            recurrenceDays: [...recurrenceDays]
        });

        if(typeof onRefreshMainTaskData === "function") {
            onRefreshMainTaskData(currentTask?.task_list_id);
        }
        if(typeof onRefreshTaskData === "function") {
            await onRefreshTaskData();
        }
    };

    return (
        <div className="task-detail-recurring-section" style={style}>
            <div className="task-detail-recurring-header">
                <label className="task-detail-field-label">Recurring</label>
                <div className="task-detail-recurring-toggle">
                    <label className="task-detail-switch">
                        <input
                            type="checkbox"
                            checked={isRecurring}
                            onChange={handleRecurringToggle}
                        />
                        <span className="task-detail-slider"></span>
                    </label>
                    <span className="task-detail-recurring-toggle-label">
                        {isRecurring ? 'Enabled' : 'Disabled'}
                    </span>
                </div>
            </div>
            
            {isRecurring && (
                <div className="task-detail-recurring-options">
                    <div className="task-detail-recurring-option-row">
                        <label className="task-detail-info-label">Repeat</label>
                        <select
                            className="task-detail-recurrence-type-select"
                            value={recurrenceType}
                            onChange={(e) => handleRecurrenceTypeChange(e.target.value)}
                        >
                            <option value="4">Daily</option>
                            <option value="1">Weekly</option>
                            <option value="2">Monthly</option>
                            <option value="3">Annually</option>
                        </select>
                    </div>
                    
                    <div className="task-detail-recurring-option-row">
                        <label className="task-detail-info-label">Every</label>
                        <div className="task-detail-recurrence-interval-wrapper">
                            <input
                                type="number"
                                className="task-detail-recurrence-interval-input"
                                min="1"
                                value={recurrenceInterval}
                                onChange={(e) => setRecurrenceInterval(parseInt(e.target.value) || 1)}
                                onBlur={(e) => handleRecurrenceIntervalChange(e.target.value)}
                            />
                            <span className="task-detail-recurrence-interval-label">
                                {recurrenceType === 4 ? 'day(s)' : 
                                 recurrenceType === 1 ? 'week(s)' : 
                                 recurrenceType === 2 ? 'month(s)' : 'year(s)'}
                            </span>
                        </div>
                    </div>

                    {/* Repeat Days for Weekly */}
                    {recurrenceType === 1 && (
                        <div className="task-detail-recurring-option-row">
                            <label className="task-detail-info-label">Repeat on</label>
                            <div className="task-detail-recurrence-days-wrapper">
                                {weekDays.map((day) => (
                                    <button
                                        key={day.index}
                                        type="button"
                                        className={`task-detail-recurrence-day-btn ${recurrenceDays.includes(day.index) ? 'active' : ''}`}
                                        onClick={() => handleRecurrenceDayToggle(day.index)}
                                        title={day.fullLabel}
                                    >
                                        {day.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="task-detail-recurring-option-row">
                        <label className="task-detail-info-label">Start date</label>
                        <div className="task-detail-recurrence-start-date-wrapper" ref={recurrenceStartDatePickerRef}>
                            {recurrenceStartDate ? (
                                <div 
                                    className="task-detail-recurrence-end-date-value"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setShowRecurrenceStartDatePicker(true);
                                    }}
                                >
                                    <div className="task-detail-icon-circle">
                                        <i className="fa-solid fa-calendar"></i>
                                    </div>
                                    <span>{new Date(recurrenceStartDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                                </div>
                            ) : (
                                <div 
                                    className="task-detail-recurrence-end-date-value"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setShowRecurrenceStartDatePicker(true);
                                    }}
                                >
                                    <div className="task-detail-icon-circle">
                                        <i className="fa-solid fa-calendar"></i>
                                    </div>
                                    <span>No start date</span>
                                </div>
                            )}
                            {showRecurrenceStartDatePicker && (
                                <div className="task-detail-due-date-picker-wrapper" onClick={(e) => e.stopPropagation()}>
                                    <input
                                        type="date"
                                        className="task-detail-due-date-picker"
                                        value={formatDateForInput(recurrenceStartDate)}
                                        onChange={(e) => handleRecurrenceStartDateChange(e.target.value)}
                                        onClick={(e) => e.stopPropagation()}
                                        autoFocus
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="task-detail-recurring-option-row">
                        <label className="task-detail-info-label">End date</label>
                        <div className="task-detail-recurrence-end-date-wrapper" ref={recurrenceEndDatePickerRef}>
                            {recurrenceEndDate ? (
                                <div 
                                    className="task-detail-recurrence-end-date-value"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setShowRecurrenceEndDatePicker(true);
                                    }}
                                >
                                    <div className="task-detail-icon-circle">
                                        <i className="fa-solid fa-calendar"></i>
                                    </div>
                                    <span>{new Date(recurrenceEndDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                                </div>
                            ) : (
                                <div 
                                    className="task-detail-recurrence-end-date-value"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setShowRecurrenceEndDatePicker(true);
                                    }}
                                >
                                    <div className="task-detail-icon-circle">
                                        <i className="fa-solid fa-calendar"></i>
                                    </div>
                                    <span>No end date</span>
                                </div>
                            )}
                            {showRecurrenceEndDatePicker && (
                                <div className="task-detail-due-date-picker-wrapper" onClick={(e) => e.stopPropagation()}>
                                    <input
                                        type="date"
                                        className="task-detail-due-date-picker"
                                        value={formatDateForInput(recurrenceEndDate)}
                                        onChange={(e) => handleRecurrenceEndDateChange(e.target.value)}
                                        onClick={(e) => e.stopPropagation()}
                                        autoFocus
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Save Button - Only show when there are changes */}
                    {hasRecurringChanges() && (
                        <div className="task-detail-recurring-save-wrapper">
                            <button
                                type="button"
                                className="task-detail-recurring-save-btn"
                                onClick={handleSaveRecurring}
                            >
                                <i className="fa-solid fa-check"></i>
                                Save changes
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default TaskRecurringSection;

